import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Check, Home } from "lucide-react";
import { HOME_THEME, primaryBtn, secondaryBtn, sectionPadding } from "../home/theme";
import { getServices, ServiceEntry } from "./servicesData";
import { translations, LanguageCode } from "../../utils/translations";

const pickOtherServices = (
  services: ServiceEntry[],
  current?: ServiceEntry
): ServiceEntry[] => services.filter((s) => s.slug !== current?.slug).slice(0, 4);

interface ServicePageProps {
  /** Set when routed from an explicit per-service route in App.tsx. */
  slug?: string;
  lang?: LanguageCode;
}

const ServicePage: React.FC<ServicePageProps> = ({ slug, lang = "en" }) => {
  const params = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const activeSlug = slug || params.slug;
  const services = getServices(lang);
  const service = services.find((s) => s.slug === activeSlug);
  const content = translations[lang].servicePage;

  if (!service) {
    return (
      <section style={{ padding: "80px 5%", textAlign: "center" }}>
        <h1 style={{ color: HOME_THEME.heroHeading }}>{content.notFoundTitle}</h1>
        <p style={{ color: HOME_THEME.muted }}>{content.notFoundText}</p>
        <Link to="/services" style={{ color: HOME_THEME.primary, fontWeight: 700 }}>
          {content.backToAll}
        </Link>
      </section>
    );
  }

  const Icon = service.icon;

  return (
    <>
      {/* HERO */}
      <section
        style={{
          padding: "44px 5% 46px",
          textAlign: "center",
          background: `linear-gradient(160deg, ${HOME_THEME.surfaceAlt} 0%, ${HOME_THEME.surface} 60%)`,
        }}
      >
        <div style={{ maxWidth: "820px", margin: "0 auto" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 14px",
              borderRadius: "999px",
              background: HOME_THEME.primarySoft,
              color: HOME_THEME.primaryDark,
              fontWeight: 800,
              fontSize: "0.75rem",
              letterSpacing: "1px",
              textTransform: "uppercase",
              marginBottom: "14px",
            }}
          >
            <Icon size={15} /> {service.group}
          </div>

          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: 800,
              lineHeight: 1.15,
              margin: "0 0 12px 0",
              color: HOME_THEME.heroHeading,
            }}
          >
            {service.title}
          </h1>

          <p
            style={{
              fontSize: "1.1rem",
              color: HOME_THEME.muted,
              lineHeight: 1.55,
              maxWidth: "640px",
              margin: "0 auto 24px",
            }}
          >
            {service.tagline}
          </p>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              style={primaryBtn}
              onClick={() => {
                const el = document.getElementById("contact");
                if (el) el.scrollIntoView({ behavior: "smooth" });
                else navigate("/#contact");
              }}
            >
              {content.talkToSpecialist}
            </button>
            <Link to="/services" style={{ textDecoration: "none" }}>
              <button style={secondaryBtn()}>{content.allServices}</button>
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section style={{ padding: "34px 5% 8px" }}>
        <p
          style={{
            maxWidth: "820px",
            margin: "0 auto",
            textAlign: "center",
            fontSize: "1.05rem",
            lineHeight: 1.65,
            color: HOME_THEME.inkSoft,
          }}
        >
          {service.intro}
        </p>
      </section>

      {/* HIGHLIGHTS */}
      <section style={{ padding: sectionPadding }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "16px",
            maxWidth: "1100px",
            margin: "0 auto",
          }}
        >
          {service.highlights.map((h) => (
            <div
              key={h.title}
              style={{
                padding: "20px",
                borderRadius: "14px",
                background: HOME_THEME.surfaceAlt,
                border: `1px solid ${HOME_THEME.border}`,
              }}
            >
              <h3
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  color: HOME_THEME.heroHeading,
                }}
              >
                {h.title}
              </h3>
              <p style={{ margin: 0, color: HOME_THEME.muted, lineHeight: 1.55 }}>
                {h.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* DELIVERABLES + USE CASES */}
      <section style={{ padding: sectionPadding, background: HOME_THEME.surfaceAlt }}>
        <div
          style={{
            maxWidth: "1000px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "28px",
          }}
        >
          <div>
            <h2 style={panelHeading}>{content.whatYouGet}</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {service.deliverables.map((item) => (
                <li key={item} style={listItem}>
                  <Check size={18} color={HOME_THEME.primary} style={{ flexShrink: 0 }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 style={panelHeading}>{content.popularUses}</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {service.useCases.map((item) => (
                <li key={item} style={listItem}>
                  <ArrowRight size={18} color={HOME_THEME.accent} style={{ flexShrink: 0 }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section style={{ padding: sectionPadding, background: HOME_THEME.surface }}>
        <h2 style={{ ...panelHeading, textAlign: "center", marginBottom: "22px" }}>
          {content.exploreMore}
        </h2>
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "16px",
          }}
        >
          {pickOtherServices(services, service).map((s) => {
            const OtherIcon = s.icon;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                style={{
                  textDecoration: "none",
                  padding: "18px",
                  borderRadius: "14px",
                  border: `1px solid ${HOME_THEME.border}`,
                  background: HOME_THEME.surfaceAlt,
                }}
              >
                <OtherIcon size={22} color={HOME_THEME.primary} />
                <div
                  style={{
                    marginTop: "10px",
                    fontWeight: 700,
                    color: HOME_THEME.heroHeading,
                  }}
                >
                  {s.title}
                </div>
                <div style={{ color: HOME_THEME.muted, fontSize: "0.85rem" }}>
                  {s.tagline}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "34px 5%", background: HOME_THEME.surfaceAlt }}>
        <div
          style={{
            maxWidth: "1000px",
            margin: "0 auto",
            padding: "32px 5%",
            textAlign: "center",
            borderRadius: "20px",
            background: `linear-gradient(135deg, ${HOME_THEME.primaryDark}, ${HOME_THEME.primary})`,
            color: "#fff",
            boxShadow: "0 30px 60px -25px rgba(1,126,132,0.6)",
          }}
        >
          <h2 style={{ margin: "0 0 8px 0", fontSize: "1.8rem", fontWeight: 900 }}>
            {content.scopeTitle.replace("{service}", service.title.toLowerCase())}
          </h2>
          <p style={{ margin: "0 auto 18px", maxWidth: "560px", opacity: 0.92, lineHeight: 1.5 }}>
            {content.scopeSubtitle}
          </p>
          <Link to="/#contact" style={{ textDecoration: "none" }}>
            <button
              style={{
                padding: "12px 26px",
                background: "#fff",
                border: "none",
                borderRadius: "10px",
                color: HOME_THEME.primaryDark,
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "0.95rem",
              }}
            >
              {content.ctaBtn}
            </button>
          </Link>
          <div style={{ marginTop: "14px" }}>
            <Link
              to="/"
              style={{
                color: "rgba(255,255,255,0.85)",
                fontSize: "0.85rem",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Home size={14} /> {content.backHome}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

const panelHeading: React.CSSProperties = {
  fontSize: "1.3rem",
  fontWeight: 800,
  color: HOME_THEME.heroHeading,
  margin: "0 0 16px 0",
};

const listItem: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  alignItems: "flex-start",
  padding: "8px 0",
  color: HOME_THEME.inkSoft,
  lineHeight: 1.5,
};

export default ServicePage;
